import React, { useState, useEffect, useCallback } from 'react';
import { useWallet } from '../context/WalletContext';
import Layout from './Layout';
import Overview from './dashboard/Overview';
import BuyToken from './dashboard/BuyToken';
import SellClaim from './dashboard/SellClaim';
// import P2PTransfer from './dashboard/P2PTransfer';   // hidden — launching later
import NetworkPage from './dashboard/Network';
import History from './dashboard/History';
import RoyaltyCard from './dashboard/RoyaltyCard';
import CopyButton from './CopyButton';
import { getBalance, getPrice, getPriceHistory, getMyRoyalty } from '../utils/api';
import './Dashboard.css';

const NAV = [
  { key: 'overview', label: 'Overview', icon: '◈' },
  { key: 'buy', label: 'Buy', icon: '↗' },
  { key: 'sell', label: 'Sell & Claim', icon: '↙' },
  // { key: 'p2p', label: 'P2P Transfer', icon: '⇄' },   // hidden — launching later
  { key: 'network', label: 'Network', icon: '⬡' },
  { key: 'rewards', label: 'Rewards', icon: '♛' },
  { key: 'history', label: 'History', icon: '☰' },
];

const Dashboard = ({ onLogout, viewAsAccount = null, readOnly = false, impersonationLabel = '', onExitImpersonation = null }) => {
  const { account: walletAccount } = useWallet();
  // In admin read-only "view as user" mode, render this user's data instead of
  // the connected wallet. Same components, same layout — only the data source differs.
  const account = viewAsAccount || walletAccount;
  const [page, setPage] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [price, setPrice] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [royalty, setRoyalty] = useState(null);
  const [royaltyDismissed, setRoyaltyDismissed] = useState(false);
  const [showUsdt, setShowUsdt] = useState(false);   // app-wide "Show USDT equivalent"

  const loadData = useCallback(async () => {
    if (!account) return;
    setLoading(true);
    try {
      const [b, p] = await Promise.all([getBalance(account), getPrice()]);
      setBalance(b); setPrice(p);
      try { const ph = await getPriceHistory(90); setPriceHistory(ph.points || []); } catch {}
      try { const rr = await getMyRoyalty(account); setRoyalty(rr); } catch {}
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [account]);

  useEffect(() => { loadData(); }, [loadData]);

  // Helpers
  const f = (n, d = 4) => (n == null) ? '0' : parseFloat(n).toFixed(d);
  const BC = price?.baseCurrency || 'BNB';
  const bcRate = price?.cryptoPrices?.[BC] || 1;
  const toBC = (usdtVal) => { if (!usdtVal) return 0; if (BC === 'USDT') return usdtVal; return bcRate > 0 ? usdtVal / bcRate : 0; };
  const fBC = (usdtVal, decimals = 4) => f(toBC(usdtVal), decimals);
  const SYM = price?.tokenSymbol || balance?.tokenSymbol || 'ROBO';
  const isNotUsdt = BC !== 'USDT';

  // One money-display rule for the whole dashboard: base-currency (BNB) value +
  // unit by default; when "Show USDT equivalent" is ticked, the USDT value shows
  // beside it in a smaller, differently-coloured span (.d-usdt-eq).
  const vBC = (usdtVal, dec = 4) => {
    const bcVal = fBC(usdtVal, dec);
    if (!isNotUsdt || !showUsdt) return <>{bcVal} {BC}</>;
    return <>{bcVal} {BC} <span className="d-usdt-eq">≈ {f(usdtVal, 2)} USDT</span></>;
  };

  const shortAddr = (a) => a ? `${a.slice(0, 6)}...${a.slice(-4)}` : '—';

  const sharedProps = { account, balance, price, f, fBC, toBC, bcRate, BC, SYM, vBC, showUsdt, isNotUsdt, onRefresh: loadData, readOnly };

  // Banner shown when an admin is viewing a user's dashboard read-only.
  const roBanner = readOnly ? (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 16px', padding: '10px 16px', borderRadius: 12, background: 'var(--accent-dim, #fde8ec)', border: '1px solid var(--accent, #ef4368)' }}>
      <span>🔒 <strong>Read-only admin view</strong>{impersonationLabel ? <> — viewing <strong>{impersonationLabel}</strong>'s account</> : ''}. Buy, Sell & Claim are disabled.</span>
      {onExitImpersonation && <button className="btn btn-sm btn-outline" onClick={onExitImpersonation} style={{ flexShrink: 0 }}>Exit view</button>}
    </div>
  ) : null;

  if (loading) {
    return <Layout nav={NAV} activeNav={page} onNavChange={setPage} onLogout={onLogout} title="TOKEN">
      <div className="page-loading"><div className="spinner" /><p className="text-3">Loading...</p></div>
    </Layout>;
  }

  return (
    <Layout nav={NAV} activeNav={page} onNavChange={setPage} onLogout={onLogout} title="TOKEN">
      {roBanner}
      {/* App-wide "Show USDT equivalent" toggle — only when base currency isn't USDT */}
      {isNotUsdt && (
        <div className="d-usdt-toggle">
          <label className="d-toggle-label">
            <input type="checkbox" checked={showUsdt} onChange={() => setShowUsdt(!showUsdt)} />
            <span>Show USDT equivalent</span>
          </label>
          {showUsdt && <span className="d-usdt-rate">1 {BC} = {f(bcRate, 2)} USDT</span>}
        </div>
      )}
      {/* ID Bar */}
      <div className="d-id-bar">
        <div className="d-id-item"><span className="d-id-label">Your Referral Code</span>
          <div className="d-id-copy">
            <span className="d-id-value mono text-accent">{balance?.referralCode || '—'}</span>
            {balance?.referralCode && <CopyButton text={balance.referralCode} title="Copy referral code" />}
          </div>
        </div>
        <div className="d-id-item"><span className="d-id-label">Wallet</span>
          <div className="d-id-copy">
            <span className="d-id-value mono" title={account || ''}>{shortAddr(account)}</span>
            {account && <CopyButton text={account} title="Copy wallet address" />}
          </div>
        </div>
        <div className="d-id-item"><span className="d-id-label">Sponsor ID</span>
          <div className="d-id-copy">
            <span className="d-id-value mono">{balance?.sponsorReferralCode || balance?.referredBy || '—'}</span>
            {(balance?.sponsorReferralCode || balance?.referredBy) && <CopyButton text={balance.sponsorReferralCode || balance.referredBy} title="Copy sponsor ID" />}
          </div>
        </div>
      </div>

      {/* Reward eligibility alert — for the last COMPLETED month (finalized tier) */}
      {royalty?.enabled && royalty?.claimable?.canClaim && !royaltyDismissed && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 16px', padding: '12px 16px', borderRadius: 12, background: 'var(--accent-dim)', border: '1px solid var(--accent)' }}>
          <span>🎉 You're eligible for the <strong style={{ color: 'var(--accent)' }}>{royalty.claimable.qualification.qualifiedTierName}</strong> royalty reward — claim it now.</span>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button className="btn btn-sm btn-primary" onClick={() => setPage('rewards')}>View &amp; Claim</button>
            <button className="btn btn-sm btn-outline" onClick={() => setRoyaltyDismissed(true)}>✕</button>
          </div>
        </div>
      )}

      {page === 'overview' && <Overview {...sharedProps} priceHistory={priceHistory} setPriceHistory={setPriceHistory} />}
      {page === 'buy' && <BuyToken {...sharedProps} />}
      {page === 'sell' && <SellClaim {...sharedProps} />}
      {/* {page === 'p2p' && <P2PTransfer {...sharedProps} />}   // hidden — launching later */}
      {page === 'network' && <NetworkPage {...sharedProps} />}
      {page === 'rewards' && <RoyaltyCard {...sharedProps} />}
      {page === 'history' && <History account={account} f={f} fBC={fBC} vBC={vBC} BC={BC} SYM={SYM} />}
    </Layout>
  );
};

export default Dashboard;